import type { HttpRequestConfig, HttpResponse } from './types'

export const REDACTED = '[REDACTED]'

// Header, query-param and `params` names whose values are credentials.
const SENSITIVE_NAME_PATTERN =
  /^(?:authorization|proxy-authorization|cookie|set-cookie)$|api[-_]?key|token|secret|password|signature/i

// `user:pass@` in the authority part of a URL. Anchored to the start, so a `//x@y` inside a path or
// query is left alone.
const URL_CREDENTIALS_PATTERN = /^([\d+.:a-z-]*\/\/)[^\s#/?@]+@/i

// One `name=value` pair in a query string.
const QUERY_PAIR_PATTERN = /([&?])([^#&=]+)=([^#&]*)/g

function redactUrl(url: string | undefined): string | undefined {
  if (typeof url !== 'string') return url
  return url
    .replace(URL_CREDENTIALS_PATTERN, '$1')
    .replaceAll(QUERY_PAIR_PATTERN, (pair, separator: string, name: string) =>
      SENSITIVE_NAME_PATTERN.test(decodeURIComponentSafe(name)) ? `${separator}${name}=${REDACTED}` : pair
    )
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function redactParams(params: HttpRequestConfig['params']): HttpRequestConfig['params'] {
  if (params instanceof URLSearchParams) {
    const copy = new URLSearchParams(params)
    for (const name of new Set(copy.keys())) {
      if (SENSITIVE_NAME_PATTERN.test(name)) copy.set(name, REDACTED)
    }
    return copy
  }
  return Object.fromEntries(
    Object.entries(params ?? {}).map(([name, value]) => [name, SENSITIVE_NAME_PATTERN.test(name) ? REDACTED : value])
  )
}

/**
 * Returns a copy of `config` with credentials removed: sensitive headers,
 * sensitive `params` and URL query values, the basic-auth password, and
 * `user:pass@` in `url`/`baseURL`. The input is not changed, so a retry that
 * re-sends the original config keeps its credentials.
 *
 * `data` is left as-is: a request body can be any shape, so callers that send
 * secrets in the body (e.g. Workglue's `redactConfig`) redact it themselves.
 */
export function redactRequestConfig(config?: HttpRequestConfig): HttpRequestConfig {
  if (!config) return {}
  const redacted: HttpRequestConfig = { ...config }

  if (config.headers) {
    redacted.headers = Object.fromEntries(
      Object.entries(config.headers).map(([name, value]) => [
        name,
        SENSITIVE_NAME_PATTERN.test(name) ? REDACTED : value
      ])
    )
  }
  if (config.auth) {
    redacted.auth = { username: config.auth.username, password: REDACTED }
  }
  if (config.params) redacted.params = redactParams(config.params)
  if (config.url !== undefined) redacted.url = redactUrl(config.url)
  if (config.baseURL !== undefined) redacted.baseURL = redactUrl(config.baseURL)

  return redacted
}

/**
 * Error thrown by the http client for network failures and non-2xx responses.
 *
 * Mirrors the shape of `AxiosError` closely enough that existing call-sites that
 * read `error.response.{data,status,headers}` keep working after the migration
 * from axios to native fetch. The `isAxiosError` flag is preserved purely for
 * back-compat with any code still doing `error.isAxiosError` checks.
 *
 * `config` and `response.config` hold a redacted copy of the request config
 * (see `redactRequestConfig`). Loggers, Sentry and the Lambda runtime serialize
 * thrown errors, so the raw config would write auth tokens to the logs (#24816).
 */
export class HttpError<T = any> extends Error {
  public config: HttpRequestConfig
  public response?: HttpResponse<T>
  public status?: number
  public code?: string
  public cause?: unknown
  public isAxiosError = true

  constructor(message: string, config: HttpRequestConfig, response?: HttpResponse<T>, code?: string, cause?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.config = redactRequestConfig(config)
    this.response = response ? { ...response, config: this.config } : response
    this.status = response?.status
    this.code = code
    // Preserve the underlying error (e.g. undici's `TypeError: fetch failed`)
    // so Sentry can report the full linked-error chain.
    if (cause !== undefined) this.cause = cause
    // Restore prototype chain (TS target ES5/ES2015 interop)
    Object.setPrototypeOf(this, HttpError.prototype)
  }
}

/** Type guard, replacing `axios.isAxiosError`. */
export function isHttpError(error: unknown): error is HttpError {
  return (
    error instanceof HttpError || (typeof error === 'object' && error !== null && (error as any).isAxiosError === true)
  )
}
